import { useEffect, useRef, type ReactNode } from 'react';

interface ModalProps {
  title: string;
  /**
   * What the dialog acts on. No longer drawn (the ticket-head strip went in the
   * UI refresh); kept optional so call sites need not change.
   */
  kind?: string;
  /**
   * `wide` for a dialog holding a list to choose from rather than a form to
   * fill in. Both sizes still stop short of the viewport on a phone.
   */
  size?: 'default' | 'wide';
  /** Called on backdrop dismiss, Escape, or a close control. */
  onDismiss: () => void;
  children: ReactNode;
}

/**
 * Native `<dialog>` wrapper — Escape handling, focus containment and the
 * backdrop come from the platform rather than a hand-rolled trap.
 */
export function Modal({ title, size = 'default', onDismiss, children }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      className={size === 'wide' ? 'modal modal--wide' : 'modal'}
      ref={dialogRef}
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
    >
      <div className="modal__panel">
        <header className="modal__header">
          <div className="modal__header-row">
            <h2 className="modal__title">{title}</h2>
            <button className="button button--ghost" type="button" onClick={onDismiss}>
              Close
            </button>
          </div>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </dialog>
  );
}
